import { useRef } from 'react';
import { MAX_NAME_LENGTH } from '@/game/view/names';

/**
 * A text input that takes the place of a label for renaming: the old name is
 * selected, Enter or blur saves, Esc cancels. Keys stay inside it (the row it
 * sits in selects on Enter/Space; Esc must not clear the selection). An empty
 * name is saved as "no name" by the caller, restoring the default label.
 */
export function InlineRename({
  value,
  label,
  onCommit,
  onCancel,
  className = '',
}: {
  /** The current name (empty when it has none). */
  value: string;
  /** Accessible name, e.g. "Rename group". */
  label: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
  className?: string;
}) {
  // Enter/Esc unmount the input, which can blur it too: finish once.
  const done = useRef(false);
  const finish = (save: boolean, name: string) => {
    if (done.current) return;
    done.current = true;
    if (save) onCommit(name);
    else onCancel();
  };
  return (
    <input
      // biome-ignore lint/a11y/noAutofocus: the input appears because the player asked to rename
      autoFocus
      aria-label={label}
      data-rename
      defaultValue={value}
      maxLength={MAX_NAME_LENGTH}
      onFocus={(e) => e.currentTarget.select()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') finish(true, e.currentTarget.value);
        else if (e.key === 'Escape') finish(false, '');
      }}
      onKeyUp={(e) => e.stopPropagation()}
      onBlur={(e) => finish(true, e.currentTarget.value)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      className={`min-w-0 rounded bg-fill px-1 text-fg outline-none ring-1 ring-accent ${className}`}
    />
  );
}
